import { useCallback } from 'react';
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react';

import { useAppDispatch, useAppSelector } from './Store';
import { getAuthState } from '../store/AuthSlice';
import {
  closeRestrictedModePrompt,
  getCoursesState,
  ICourseSubscriptionPayment,
  openRestrictedModePrompt,
} from '../store/V2/CoursesSlice';
import { recordRestrictedModeExperimentResponse } from '../store/V2/CoursesSlice/api';
import {
  COURSE_PAGE_RESTRICTED_MODE_FEATURE_FLAG,
  COURSE_PAGE_RESTRICTED_MODE_VARIANTS,
  COURSE_SUBSCRIPTION_PAYMENT_STATES,
  TRestrictedModePromptSource,
  TRestrictedModeResponseAction,
} from '../constants/coursePaymentRestriction';
import {
  postHogCourseRestrictedModePromptOpened,
  postHogCourseRestrictedModeResponded,
} from '../helper/PostHogHelper';

export type TCourseRestrictedModeExperiment = {
  /** PostHog variant for event properties ('control' | 'test' | 'unknown'). */
  variant: string;
  /** The course the state belongs to (`userCourse._id`). */
  courseId?: string;
  payment?: ICourseSubscriptionPayment;
  /**
   * The experiment is showing: the student is in the test variant AND their
   * renewal has failed at least twice (state RESTRICTED). Control, tutors and
   * students whose payments are fine all read false.
   */
  isActive: boolean;
  /** Track and open the prompt from one of the three surfaces. */
  openPrompt: (source: TRestrictedModePromptSource) => void;
  /**
   * Record the student's answer (Google Chat + PostHog) and close the prompt.
   * `reason` is the free-text answer collected for WONT_RENEW.
   */
  respond: (
    source: TRestrictedModePromptSource,
    action: TRestrictedModeResponseAction,
    reason?: string
  ) => void;
};

/**
 * Everything the course page needs for the `course_page_restricted_mode`
 * painted-door experiment. Reads the failed-renewal state the backend put on
 * the course and the PostHog variant; nothing here blocks a real action.
 */
export const useCourseRestrictedModeExperiment = (): TCourseRestrictedModeExperiment => {
  const dispatch = useAppDispatch();
  const posthog = usePostHog();
  const flagVariant = useFeatureFlagVariantKey(
    COURSE_PAGE_RESTRICTED_MODE_FEATURE_FLAG
  );

  const { user: authUser } = useAppSelector(getAuthState);
  const { userCourse } = useAppSelector(getCoursesState);

  const payment = userCourse?.subscriptionPayment;
  const variant = typeof flagVariant === 'string' ? flagVariant : 'unknown';
  const isActive =
    variant === COURSE_PAGE_RESTRICTED_MODE_VARIANTS.TEST &&
    payment?.state === COURSE_SUBSCRIPTION_PAYMENT_STATES.RESTRICTED;

  const userId = authUser?._id;
  const courseId = userCourse?._id;

  const openPrompt = useCallback(
    (source: TRestrictedModePromptSource) => {
      postHogCourseRestrictedModePromptOpened(posthog, {
        userId,
        courseId,
        variant,
        paymentState: payment?.state,
        failedAttempts: payment?.failedAttempts,
        source,
      });
      dispatch(openRestrictedModePrompt(source));
    },
    [posthog, dispatch, userId, courseId, variant, payment]
  );

  const respond = useCallback(
    (
      source: TRestrictedModePromptSource,
      action: TRestrictedModeResponseAction,
      reason?: string
    ) => {
      postHogCourseRestrictedModeResponded(posthog, {
        userId,
        courseId,
        variant,
        paymentState: payment?.state,
        failedAttempts: payment?.failedAttempts,
        source,
        action,
        reason,
      });

      if (courseId) {
        // Fire-and-forget: the prompt closes immediately either way.
        dispatch(
          recordRestrictedModeExperimentResponse({
            courseId,
            action,
            source,
            variant,
            reason,
          })
        );
      }

      dispatch(closeRestrictedModePrompt());
    },
    [posthog, dispatch, userId, courseId, variant, payment]
  );

  return {
    variant,
    courseId,
    payment,
    isActive,
    openPrompt,
    respond,
  };
};

export default useCourseRestrictedModeExperiment;
